import { translateSelector } from '@ackee/jerome';
import { createSelector } from '@reduxjs/toolkit';

import { ListItemType, SecurityAge, SecurityLeaks, SecurityLevel, SecurityReport } from '../../constants';
import type {
    SecurityAuditAgeSecret,
    SecurityAuditDuplicitiesGroup,
    SecurityAuditDuplicitiesSecret,
    SecurityAuditLeaksSecret,
    SecurityAuditLevelSecret,
    SecurityAuditReportItem,
} from '../../types';
import { getSecurityAuditSecretsWithAgeGroups, selectSecurityAuditAgeSortedListSecrets } from './auditAgeList';
import { getSecurityAuditSecretsWithLeaksGroups, selectSecurityAuditLeaksSortedListSecrets } from './auditLeaksList';
import { getSecurityAuditSecretsWithLevelGroups, selectSecurityAuditLevelSortedListSecrets } from './auditLevelList';
import { selectPasswordSecrets } from './list';
import { compareByName, getSecretsWithGroups } from './utilities';

const selectSecurityAuditReportDuplicitiesSecrets = createSelector([selectPasswordSecrets, translateSelector], (secrets, { locale }) =>
    secrets
        .map<SecurityAuditDuplicitiesSecret>(secret => ({
            ...secret,
            listItemType: ListItemType.SECRET,
            key: `${secret.id}-${SecurityReport.DUPLICITIES}`,
        }))
        .map<SecurityAuditDuplicitiesSecret>((secret, _, prevSecrets) => ({
            ...secret,
            duplicities: prevSecrets.reduce((count, otherSecret) => {
                if (
                    // Do not count itself
                    secret.id !== otherSecret.id &&
                    // Do not count duplicities for empty passwords
                    secret.passwordHashed &&
                    // Count matching hashes
                    secret.passwordHashed === otherSecret.passwordHashed
                ) {
                    return (count += 1);
                }
                return count;
            }, 0),
        }))
        .filter(secret => secret.duplicities && !secret.ignoreSecurityReport)
        .sort(function (secretA, secretB) {
            return secretB.duplicities! - secretA.duplicities! || compareByName(secretA, secretB, locale);
        }),
);

const selectSecurityAuditReportHighAgeSecrets = createSelector(
    selectSecurityAuditAgeSortedListSecrets,
    (secrets): SecurityAuditAgeSecret[] =>
        secrets.filter(secret => secret.securityAge === SecurityAge.MORE_THAN_TWO_YEARS && !secret.ignoreSecurityReport),
);

const selectSecurityAuditReportLowLevelSecrets = createSelector(
    selectSecurityAuditLevelSortedListSecrets,
    (secrets): SecurityAuditLevelSecret[] =>
        secrets.filter(secret => secret.securityLevel! <= SecurityLevel.AVERAGE && !secret.ignoreSecurityReport),
);

const selectSecurityAuditReportManyLeaksSecrets = createSelector(
    selectSecurityAuditLeaksSortedListSecrets,
    (secrets): SecurityAuditLeaksSecret[] =>
        secrets.filter(secret => secret.securityLeaks === SecurityLeaks.MANY_LEAKS && !secret.ignoreSecurityReport),
);

const getSecurityAuditSecretsWithDuplicitiesGroups = (secrets: SecurityAuditDuplicitiesSecret[]) =>
    getSecretsWithGroups(
        secrets,
        () => false,
        (): SecurityAuditDuplicitiesGroup => ({
            key: 'duplicities',
            listItemType: ListItemType.GROUP,
            count: 0,
        }),
        group => {
            group.count += 1;
        },
    );

export const selectSecurityAuditSecretsWithReportGroups = createSelector(
    [
        selectSecurityAuditReportLowLevelSecrets,
        selectSecurityAuditReportHighAgeSecrets,
        selectSecurityAuditReportManyLeaksSecrets,
        selectSecurityAuditReportDuplicitiesSecrets,
    ],
    (lowLevelSecrets, highAgeSecrets, manyLeaksSecrets, duplicitiesSecrets) => {
        let list: SecurityAuditReportItem[] = [];

        if (lowLevelSecrets.length > 0) {
            list = getSecurityAuditSecretsWithLevelGroups(lowLevelSecrets);
        }

        if (highAgeSecrets.length > 0) {
            list = [...list, ...getSecurityAuditSecretsWithAgeGroups(highAgeSecrets)];
        }

        if (manyLeaksSecrets.length > 0) {
            list = [...list, ...getSecurityAuditSecretsWithLeaksGroups(manyLeaksSecrets)];
        }

        if (duplicitiesSecrets.length > 0) {
            list = [...list, ...getSecurityAuditSecretsWithDuplicitiesGroups(duplicitiesSecrets)];
        }

        return list;
    },
);
